import * as React from 'react';
import {
  BaSeButton,
  BaSeTheme,
  BaSeDialogContext,
  BaSeLoading,
} from '@base/react';
import { Footer, FooterButton, FormContainer } from './create-form-styled';
import { Uploader } from '../shared/upload/uploader';
import { usePublicoService } from '../../hooks/pulblico-service';
import { useFuncionalidadeService } from '../../hooks/funcionalidade-service';
import { useGetPortfolio } from '../../hooks/produtos';
import * as Sebrae from '@sebrae/react-core';
import { TEMAS_ATIVOS_ENDPOINT, TEMAS_ENDPOINT, PRODUTO_ENDPOINT } from '../../hooks/constants';
import { useProductForm } from './hooks/useProductForm';
import { useProductImages } from './hooks/useProductImages';
import { useProductValidation } from './hooks/useProductValidation';
import { BasicInfoSection } from './sections/BasicInfoSection';
import { DetailedInfoSection } from './sections/DetailedInfoSection';
import { DurationSection } from './sections/DurationSection';
import { LinksAndImagesSection } from './sections/LinksAndImagesSection';
import { TagsAndThemesSection } from './sections/TagsAndThemesSection';
import { getPortfolioModalConfig } from './modal/getPortfolioModal';

const LOADING_STATES = {
  IDLE: -1,
  SAVING: 0,
  SAVING_AND_CREATE: 1,
  FETCHING_PRODUCT: 2,
  FETCHING_TAGS: 4,
  FETCHING_PORTFOLIO: 5,
};

const filterActiveOrSelectedOptions = (options, selectedOptions, isUpdate) => {
  if (!isUpdate) {
    return options;
  }

  return options?.filter(
    (item) => item?.ativo || item?.id in (selectedOptions ?? {}),
  );
};

const normalizeThemeName = (name) => (name ?? '').trim().toLocaleLowerCase();

const getPortfolioThemes = (portfolio) =>
  portfolio.temasSubtemasRelacionados?.temas
    ?.map((item) => item?.tema)
    ?.filter(Boolean) || [];

const selectThemesByName = (availableThemes, portfolioThemes) => {
  const portfolioThemeNames = new Set(
    portfolioThemes.map((theme) => normalizeThemeName(theme?.nome)),
  );

  return (
    availableThemes?.reduce((selectedThemes, theme) => {
      if (portfolioThemeNames.has(normalizeThemeName(theme?.nome))) {
        selectedThemes[theme.id] = theme.id;
      }

      return selectedThemes;
    }, {}) || {}
  );
};

export const CreateProduto = ({
  getFotoCapa,
  getFotoCard,
  save,
  isUpdate,
  notify,
  openModal,
  typeProducts,
  productProps,
  setIsFormProduto,
  update,
  setIsUpdate,
  setTitleFormProduto,
  callback,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const { backendGet } = Sebrae.useBackend();
  const { id } = productProps ?? {};
  const productId = productProps?.id;
  const { showNotification } = React.useContext(BaSeDialogContext);
  const { getObterPublico } = usePublicoService();
  const { getObterFuncionalidades, getObterFuncionalidadesAtivas } = useFuncionalidadeService();

  const [portfolioIdToFetch, setPortfolioIdToFetch] = React.useState(null);
  const [portfolioData, portfolioLoading, portfolioError] =
    useGetPortfolio(portfolioIdToFetch);

  const [controlId, setControlId] = React.useState(null);
  const [availableOptions, setAvailableOptions] = React.useState({
    publico: [],
    funcionalidades: [],
    temas: [],
  });
  const [uiState, setUiState] = React.useState({
    loading: productProps?.id
      ? LOADING_STATES.FETCHING_PRODUCT
      : LOADING_STATES.IDLE,
    sent: false,
    isNovoProduto: !isUpdate,
  });

  const formRef = React.useRef(null);
  const hasLoadedCardImageRef = React.useRef(false);
  const hasLoadedCapaImageRef = React.useRef(false);

  const {
    formData,
    setFormData,
    updateField,
    toggleCheckbox,
    addTag,
    deleteTag,
    addModule,
    removeModule,
    isCompleteForm,
  } = useProductForm(productProps, isUpdate);

  const {
    imageState,
    setImageState,
    isValidImageFormat,
    handleImageCapaUpload,
    handleImageCardUpload,
  } = useProductImages(isUpdate);

  const { validateForm, createProductDTO } = useProductValidation(
    formData,
    isCompleteForm,
    notify,
  );

  const handleSave = React.useCallback(
    async (createNew = false) => {
      if (!validateForm()) {
        setUiState((prev) => ({ ...prev, sent: true }));
        return;
      }

      setUiState((prev) => ({
        ...prev,
        loading: createNew
          ? LOADING_STATES.SAVING_AND_CREATE
          : LOADING_STATES.SAVING,
      }));

      const dto = createProductDTO(imageState, isUpdate, id);
      const { sucess, message } = isUpdate
        ? await update(dto)
        : await save(dto);

      if (sucess) {
        notify({
          message: 'Dados salvos com sucesso!',
          color: 'confirmation',
        });
        setIsUpdate(false);
        if (createNew) {
          callback();
          setTitleFormProduto('Novo produto');
        } else {
          setIsFormProduto(false);
        }
      } else {
        setUiState((prev) => ({ ...prev, sent: true }));
        notify({ message, color: 'destructive' });
      }

      setUiState((prev) => ({ ...prev, loading: LOADING_STATES.IDLE }));
    },
    [
      validateForm,
      createProductDTO,
      imageState,
      isUpdate,
      id,
      update,
      save,
      notify,
      setIsUpdate,
      setIsFormProduto,
      callback,
      setTitleFormProduto,
    ],
  );

  const arrayToObj = React.useCallback(
    (arr) =>
      arr?.reduce(
        (acc, curr) => (curr ? { ...acc, [curr.id]: curr.id } : acc),
        {},
      ) || {},
    [],
  );

  const hasFormData = React.useCallback(() => {
    return !!(
      formData.nome ||
      formData.descricao ||
      formData.objetivo ||
      formData.tags?.length ||
      formData.valor
    );
  }, [formData]);

  const fillFormWithPortfolioData = React.useCallback(
    (data) => {
      if (!data) return;

      const temasObj = selectThemesByName(
        availableOptions.temas,
        getPortfolioThemes(data),
      );

      setFormData((prev) => ({
        ...prev,
        nome: data.nome || prev.nome,
        descricao: data.descricao || prev.descricao,
        objetivo: data.objetivo || prev.objetivo,
        valor: data.valor || prev.valor,
        tags: data.tags || prev.tags,
        temasSelecionadas: temasObj,
      }));

      notify({
        message: 'Dados do portfólio carregados com sucesso!',
        color: 'confirmation',
      });
    },
    [availableOptions.temas, setFormData, notify],
  );

  const handleSearchPortfolio = React.useCallback(() => {
    if (!formData.idPortfolio) {
      notify({
        message: 'Informe o código do portfólio',
        color: 'attention',
      });
      return;
    }

    if (hasFormData()) {
      openModal(
        getPortfolioModalConfig(
          () => setPortfolioIdToFetch(formData.idPortfolio),
          () => {},
        ),
      );
    } else {
      setPortfolioIdToFetch(formData.idPortfolio);
    }
  }, [formData.idPortfolio, hasFormData, openModal, notify]);

  const createImageModalConfig = React.useCallback(
    (imagem, title, onSave, imageType) => ({
      title: `Selecione a imagem ${title}`,
      content: (contentData) => (
        <Uploader
          foto={imagem}
          forceClose={contentData}
          onSave={onSave}
          notify={notify}
        />
      ),
      afterClose: () =>
        setImageState((prev) => ({ ...prev, [`${imageType}Loaded`]: null })),
      size: 'large',
      actions: [],
    }),
    [notify, setImageState],
  );

  const handleShowImageModal = React.useCallback(
    (file, imageType) => {
      if (!file) {
        if (imageType === 'imagem') {
          setImageState((prev) => ({
            ...prev,
            imagem: null,
            updatedImage: true,
          }));
        } else {
          setImageState((prev) => ({
            ...prev,
            imagemCard: null,
            updatedImageCard: true,
          }));
        }
        return;
      }

      if (!isValidImageFormat(file)) {
        notify({
          supportMessage: 'A imagem não está no formato JPEG, PNG ou SVG.',
          message: 'Formato da imagem inválido',
          color: 'destructive',
        });
        return;
      }
      if (imageType === 'imagem') {
        handleImageCapaUpload(file);
      } else {
        handleImageCardUpload(file);
      }
    },
    [
      isValidImageFormat,
      handleImageCapaUpload,
      handleImageCardUpload,
      notify,
      setImageState,
    ],
  );

  const handleShowImageCapaModal = React.useCallback(
    (file) => handleShowImageModal(file, 'imagem'),
    [handleShowImageModal],
  );

  const handleShowImageCardModal = React.useCallback(
    (file) => handleShowImageModal(file, 'imagemCard'),
    [handleShowImageModal],
  );

  const selectedProductTypeId = React.useMemo(
    () =>
      typeProducts.find((type) => type?.id === formData.tipoProduto?.id)?.id,
    [typeProducts, formData.tipoProduto],
  );

  const visibleOptions = React.useMemo(
    () => ({
      ...availableOptions,
      funcionalidades: filterActiveOrSelectedOptions(
        availableOptions.funcionalidades,
        formData.funcionalidades,
        isUpdate,
      ),
      temas: filterActiveOrSelectedOptions(
        availableOptions.temas,
        formData.temasSelecionadas,
        isUpdate,
      ),
    }),
    [
      availableOptions,
      formData.funcionalidades,
      formData.temasSelecionadas,
      isUpdate,
    ],
  );

  React.useEffect(() => {
    if (portfolioData) {
      fillFormWithPortfolioData(portfolioData);
      setPortfolioIdToFetch(null);
    }
  }, [portfolioData, fillFormWithPortfolioData]);

  React.useEffect(() => {
    if (portfolioError) {
      notify({
        message: 'Erro ao buscar dados do portfólio',
        supportMessage: 'Verifique se o código está correto',
        color: 'destructive',
      });
      setPortfolioIdToFetch(null);
    }
  }, [portfolioError, notify]);

  React.useEffect(() => {
    if (portfolioLoading) {
      setUiState((prev) => ({
        ...prev,
        loading: LOADING_STATES.FETCHING_PORTFOLIO,
      }));
    } else {
      setUiState((prev) => ({ ...prev, loading: LOADING_STATES.IDLE }));
    }
  }, [portfolioLoading]);

  React.useEffect(() => {
    if (imageState.imageLoaded) {
      openModal(
        createImageModalConfig(
          imageState.imageLoaded,
          'da capa',
          (img) =>
            setImageState((prev) => ({
              ...prev,
              imagem: img,
              updatedImage: true,
            })),
          'image',
        ),
      );
    }
  }, [
    imageState.imageLoaded,
    openModal,
    createImageModalConfig,
    setImageState,
  ]);

  React.useEffect(() => {
    if (imageState.imageCardLoaded) {
      openModal(
        createImageModalConfig(
          imageState.imageCardLoaded,
          'de card',
          (img) =>
            setImageState((prev) => ({
              ...prev,
              imagemCard: img,
              updatedImageCard: true,
            })),
          'imageCard',
        ),
      );
    }
  }, [
    imageState.imageCardLoaded,
    openModal,
    createImageModalConfig,
    setImageState,
  ]);

  React.useEffect(() => {
    if (productProps?.id && !controlId && isUpdate) {
      setControlId(productProps?.id);
      backendGet(`${PRODUTO_ENDPOINT}/${productProps?.id}`)
        .then((data) => {
          setFormData((prev) => ({
            ...prev,
            idPortfolio: data.idPortfolio ?? '',
            idDisponibilizacao: data.idDisponibilizacao ?? '',
            codigoProdutoSas: data.codigo ?? data.codigoProdutoSas ?? '',
            codigoDisponibilizacaoSas: data.codigoDisponibilizacaoSas ?? '',
            descricao: data.descricao ?? '',
            objetivo: data.objetivo ?? '',
            cargaHoraria: data.cargaHoraria ? `${data.cargaHoraria} horas` : '',
            tempoRealizacao: data.tempoRealizacao
              ? `${data.tempoRealizacao} dias`
              : '',
            valor: data.valor ?? '',
            listaModulo: data.modulos ?? [],
            ativo: data.ativo,
            publico: arrayToObj(data.publicos),
            funcionalidades: arrayToObj(data.funcionalidades),
            isLoginWhatsapp: data.exigeLoginPrevioWhatsapp,
            linkWhatsapp: data.linkAcessoWhatsapp ?? '',
            linkTelegran: data.linkAcessoTelegram ?? '',
            isLoginTelegran: data.exigeLoginPrevioTelegram,
            url: data.urlVideoMarketing ?? '',
            tags: data.tags ?? [],
            temasSelecionadas: arrayToObj(data.temas),
          }));
        })
        .catch((error) => {
          notify({
            message: error?.message ?? 'Erro ao buscar produto',
            color: 'destructive',
          });
        })
        .finally(() =>
          setUiState((prev) => ({ ...prev, loading: LOADING_STATES.IDLE })),
        );
    } else {
      setUiState((prev) => ({ ...prev, loading: LOADING_STATES.IDLE }));
    }
  }, [
    productProps,
    backendGet,
    controlId,
    notify,
    isUpdate,
    arrayToObj,
    setFormData,
  ]);

  React.useEffect(() => {
    hasLoadedCardImageRef.current = false;
    hasLoadedCapaImageRef.current = false;
  }, [productId]);

  React.useEffect(() => {
    if (getFotoCapa) {
      if (hasLoadedCapaImageRef.current) {
        return;
      }
      setImageState((prev) => ({ ...prev, loadingImage: true }));
      getFotoCapa()
        .then((data) => {
          hasLoadedCapaImageRef.current = true;
          setImageState((prev) => ({
            ...prev,
            imagem: prev.updatedImage ? prev.imagem : data,
            updatedImage: prev.updatedImage ? prev.updatedImage : false,
          }));
        })
        .finally(() =>
          setImageState((prev) => ({ ...prev, loadingImage: false })),
        );
    }
  }, [getFotoCapa, setImageState]);

  React.useEffect(() => {
    if (getFotoCard) {
      if (hasLoadedCardImageRef.current) {
        return;
      }
      setImageState((prev) => ({ ...prev, loadingImage: true }));
      getFotoCard()
        .then((data) => {
          hasLoadedCardImageRef.current = true;
          setImageState((prev) => ({
            ...prev,
            imagemCard: prev.updatedImageCard ? prev.imagemCard : data,
            updatedImageCard: prev.updatedImageCard
              ? prev.updatedImageCard
              : false,
          }));
        })
        .finally(() =>
          setImageState((prev) => ({ ...prev, loadingImage: false })),
        );
    }
  }, [getFotoCard, setImageState]);

  React.useEffect(() => {
    getObterPublico()
      .then((list) => {
        setAvailableOptions((prev) => ({
          ...prev,
          publico:
            list?.map((item) => ({
              id: item.id,
              label: item.nome,
              ativo: item.ativo,
            })) || [],
        }));
      })
      .catch((error) =>
        showNotification({
          message: 'Erro ao recuperar a lista de Público',
          color: 'destructive',
          supportMessage: error.statusCode,
        }),
      );
  }, [getObterPublico, showNotification]);

  React.useEffect(() => {
    const fetchFuncionalidades = isUpdate
      ? getObterFuncionalidades
      : getObterFuncionalidadesAtivas;

    fetchFuncionalidades()
      .then((list) => {
        setAvailableOptions((prev) => ({
          ...prev,
          funcionalidades: list || [],
        }));
      })
      .catch((error) =>
        showNotification({
          message: 'Erro ao recuperar a lista de Funcionalidades',
          color: 'destructive',
          supportMessage: error.statusCode,
        }),
      );
  }, [
    getObterFuncionalidades,
    getObterFuncionalidadesAtivas,
    isUpdate,
    showNotification,
  ]);

  React.useEffect(() => {
    const endpoint = isUpdate ? TEMAS_ENDPOINT : TEMAS_ATIVOS_ENDPOINT;

    backendGet(endpoint)
      .then((data) => {
        setAvailableOptions((prev) => ({ ...prev, temas: data || [] }));
      })
      .catch(() => setAvailableOptions((prev) => ({ ...prev, temas: [] })));
  }, [backendGet, isUpdate]);

  if (uiState.loading === LOADING_STATES.FETCHING_PRODUCT) {
    return <BaSeLoading text="Buscando dados do produto" />;
  }

  if (uiState.loading === LOADING_STATES.FETCHING_PORTFOLIO) {
    return <BaSeLoading text="Buscando dados do portfólio" />;
  }

  return (
    <FormContainer ref={formRef}>
      <BasicInfoSection
        formData={formData}
        typeProducts={typeProducts}
        isUpdate={isUpdate}
        uiState={uiState}
        onFieldChange={updateField}
        selectedProductTypeId={selectedProductTypeId}
        onSearchPortfolio={handleSearchPortfolio}
      />
      {isCompleteForm && (
        <DetailedInfoSection
          formData={formData}
          availableOptions={visibleOptions}
          onFieldChange={updateField}
          onToggleCheckbox={toggleCheckbox}
          onAddModule={addModule}
          onRemoveModule={removeModule}
        />
      )}
      <DurationSection
        formData={formData}
        availableOptions={visibleOptions}
        isCompleteForm={isCompleteForm}
        onFieldChange={updateField}
        onToggleCheckbox={toggleCheckbox}
        setFormData={setFormData}
      />
      <LinksAndImagesSection
        formData={formData}
        imageState={imageState}
        isCompleteForm={isCompleteForm}
        uiState={uiState}
        onFieldChange={updateField}
        onShowImageCapaModal={handleShowImageCapaModal}
        onShowImageCardModal={handleShowImageCardModal}
      />
      {isCompleteForm && (
        <TagsAndThemesSection
          formData={formData}
          availableOptions={visibleOptions}
          uiState={uiState}
          onFieldChange={updateField}
          onToggleCheckbox={toggleCheckbox}
          onAddTag={addTag}
          onDeleteTag={deleteTag}
        />
      )}
      <Footer>
        <FooterButton>
          <BaSeButton
            isLoading={uiState.loading === LOADING_STATES.SAVING}
            value="Salvar"
            onClick={() => handleSave(false)}
          />
          <BaSeButton
            isLoading={uiState.loading === LOADING_STATES.SAVING_AND_CREATE}
            type="secondary"
            value="Salvar e criar novo"
            color={BaSeTheme.colors.institucionais.azulSebrae36}
            onClick={() => handleSave(true)}
          />
        </FooterButton>
        <BaSeButton
          type="secondary"
          color={BaSeTheme.colors.institucionais.azulSebrae36}
          value="Cancelar"
          onClick={() => {
            setIsUpdate(false);
            setIsFormProduto(false);
          }}
        />
      </Footer>
    </FormContainer>
  );
};
